import type React from 'react';

import DescriptionIcon from '@mui/icons-material/Description';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useLocation } from 'react-router';

import { RoleTypeDto } from '@/api/generated/models/role-type-dto';
import { useLogout } from '@/api/useLogout.util';
import { useNotificationsContext } from '@/contexts/notifications/Notifications.context.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { FeedbackModal } from '@/layouts/private/topBar/components/FeedbackDialog.comp.tsx';
import { TopBarLanguageChange } from '@/layouts/private/topBar/components/languageChange/TopBarLanguageChange.comp.tsx';
import { TopBarItemsStyles, TopBarStyles } from '@/layouts/private/topBar/TopBar.styles.ts';
import { TopBarBreadcrumbs } from '@/layouts/private/topBar/TopBarBreadcrumbs.comp.tsx';
import { RouteEnum } from '@/layouts/routes/Route.model';
import { PATHS_NOT_REQUIRING_AUTHORIZATION } from '@/model/routes.consts.ts';
import { useOcdHistory } from '@/utils/useOcdHistory.util';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

import { isAuthorized } from '../authorization';

/**
 * App-layout-only topbar (OCD-308): login and assessment detail never mount
 * this component (BareLayout). System paths that stay in the app group and
 * skip chrome (maintenance, not-authorized, waiting-for-approval) still bail
 * out via the authorization path list.
 */
export const TopBar: React.FC = () => {
  const { logout } = useLogout();
  const {
    user: { role },
  } = useUserContext();
  const { t: tTopBar } = useTranslationWithPrefix('layouts.topBar.tooltip');
  const history = useOcdHistory();
  const { closeFeedbackDialog, isFeedbackDialogVisible, openFeedbackDialog } =
    useNotificationsContext();
  const location = useLocation();

  const canSeeItsProfile = !isAuthorized(
    [RoleTypeDto.ExternalPartner, RoleTypeDto.GroupExternalPartner],
    role,
  );

  const handleLogout = async () => {
    await logout();
  };

  const handleOpenGuide = () => {
    const win = window.open('/KnowMe_OCD-Manual.pdf', '_blank');
    if (win != null) {
      win.focus();
    }
  };

  const handleOpenProfilePage = () => {
    history.push(RouteEnum.MY_PROFILE);
  };

  if (PATHS_NOT_REQUIRING_AUTHORIZATION.includes(location.pathname)) {
    return null;
  }

  return (
    <>
      <Grid container sx={TopBarStyles}>
        <Grid size={6}>
          <TopBarBreadcrumbs />
        </Grid>
        <Grid>
          <Grid container sx={TopBarItemsStyles}>
            <Grid>
              <Tooltip title={tTopBar('feedback')}>
                <IconButton onClick={openFeedbackDialog} size="large">
                  <SupportAgentIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            {canSeeItsProfile && (
              <Grid>
                <Tooltip title={tTopBar('profile')}>
                  <IconButton
                    className="tour-profile-icon"
                    onClick={handleOpenProfilePage}
                    size="large"
                  >
                    <PersonIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            <Grid>
              <Tooltip title={tTopBar('userGuide')}>
                <IconButton onClick={handleOpenGuide} size="large">
                  <DescriptionIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid>
              <TopBarLanguageChange />
            </Grid>
            <Grid>
              <Tooltip title={tTopBar('logout')}>
                <IconButton onClick={handleLogout} size="large">
                  <LogoutOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {isFeedbackDialogVisible && <FeedbackModal onClose={closeFeedbackDialog} />}
    </>
  );
};
